<template>
	<div class="positioning-card" :class="[`positioning-card--${line.kind}`, { 'positioning-card--expanded': expanded }]">
		<div class="positioning-card__header" role="button" tabindex="0" :aria-expanded="expanded" @click="emit('toggle')" @keydown.enter.prevent="emit('toggle')" @keydown.space.prevent="emit('toggle')">
			<ProductCellRenderer class="positioning-card__title" :params="titleParams" />
			<template v-if="!expanded">
				<span class="positioning-card__pdl" :class="{ 'positioning-card__pdl--top': line.isTopPdl }">{{ line.display.pdl }}</span>
				<v-progress-linear class="positioning-card__bar" :model-value="line.absent ? 0 : (line.pdl ?? 0)" :max="100" :color="line.isTopPdl ? 'success' : 'primary'" bg-color="on-surface" :bg-opacity="0.1" height="8" rounded />
			</template>
			<v-icon class="positioning-card__chevron">{{ expanded ? 'expand_less' : 'expand_more' }}</v-icon>
		</div>

		<div v-if="expanded" class="positioning-card__tiles metric-tile-grid" style="--metric-tile-grid-columns: 3">
			<MetricTile v-for="column in POSITIONING_COLUMNS" :key="column.key" large :label="t(column.labelKey)" :color="column.key === 'pdl' && line.isTopPdl ? 'success' : null">
				<v-icon v-if="column.key === 'presentTheoretical' && line.showBalance" size="18">balance</v-icon>
				{{ line.display[column.key] }}
			</MetricTile>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import ProductCellRenderer from '@/components/agGrid/ProductCellRenderer.vue';
import MetricTile from '@/components/common/MetricTile.vue';
import { POSITIONING_COLUMNS } from './positioningDisplay';

const props = defineProps({
	line: { type: Object, required: true },
	expanded: { type: Boolean, default: false },
});
const emit = defineEmits(['toggle']);

const t = useTranslator();

function tagClass(tag) {
	return tag.color === 'error' ? 'positioning-card__tag--error' : 'positioning-card__tag--primary';
}

// ProductCellRenderer owns the title + subtitle + trailing tags layout already used across the app's
// AgGrid/touch-card screens (see CatchmentComparisonProductCard) - reused here instead of a bespoke header.
const titleParams = computed(() => ({
	value: props.line.name,
	subtitle: props.line.caption,
	icon: false,
	// A subset's caption already says "sous-ensemble Ma marque" - its own tags (if any) would be redundant.
	tags: props.line.caption ? [] : (props.line.tags || []).map((tag) => ({ text: tag.text, class: tagClass(tag) })),
}));
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.positioning-card {
	border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
	border-radius: 12px;
	background: rgb(var(--v-theme-surface));
	overflow: hidden;

	&--myBrand {
		box-shadow: inset 4px 0 0 rgb(var(--v-theme-primary));
	}

	&--subset {
		box-shadow: inset 4px 0 0 rgba(var(--v-theme-primary), 0.3);
	}

	&__header {
		display: flex;
		align-items: center;
		gap: 10px;
		width: 100%;
		padding: 14px 12px 14px 20px;
		cursor: pointer;
		user-select: none;
	}

	&__title {
		flex: 1 1 auto;
		min-width: 0;
	}

	:deep(.product-cell-renderer__name) {
		font-size: $font-size-large;
		font-weight: 700;
		color: $color-primary;
	}

	:deep(.positioning-card__tag--primary) {
		color: rgb(var(--v-theme-primary));
		border-color: rgb(var(--v-theme-primary)) !important;
	}

	:deep(.positioning-card__tag--error) {
		color: rgb(var(--v-theme-error));
		border-color: rgb(var(--v-theme-error)) !important;
	}

	&__pdl {
		flex: none;
		min-width: 44px;
		text-align: right;
		font-size: $font-size-large;
		font-weight: 700;
		color: $color-primary;

		&--top {
			color: rgb(var(--v-theme-success));
		}
	}

	&__bar {
		flex: 0 0 96px;
		width: 96px !important;
	}

	&__chevron {
		flex: none;
		color: rgba(var(--v-theme-on-surface), 0.5);
	}

	&__tiles {
		padding: 0 12px 14px 20px;
		color: $color-primary;
	}
}
</style>
